import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Stack,
  PaperProps,
  Chip,
  Button,
  Typography,
  BoxProps,
  StackProps,
  IconProps,
  TypographyProps,
  ChipProps,
  Tooltip,
  ButtonProps,
} from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import PercentIcon from '@mui/icons-material/Percent';
import AlarmIcon from '@mui/icons-material/Alarm';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { isMobileScreen } from '../../../helper';

import MobileHeader from '../../../components/Mobile/Header';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import TopicHeader from '../../../components/Headers/TopicHeader';
import ShareModal, { IShareParentState } from './modals/ShareModal';
import LessonPlanTable from './components/LessonPlanTable';

import {
  getGroupedSubTopics,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import {
  getDiagnosticById,
  getDiagnosticState,
  getRecommendation,
  requestCallback,
  shareWithParent,
} from '../../../store/DiagnosticSlice';
import {
  createSession,
  getProductPrice,
  getStripeState,
} from '../../../store/StripeSlice';
import { getAuthState } from '../../../store/AuthSlice';

const whatsappIconStyles = {
  height: '21px',
  width: '21px',
  backgroundColor: TP_COLORS.white,
  color: '#3FD146',
  borderRadius: '4px',
  padding: '2px',
};

const secondaryButtonProps: ButtonProps['sx'] = {
  size: 'small',
  color: 'inherit',
  variant: 'contained',
  sx: {
    color: TP_COLORS.black,
    backgroundColor: TP_COLORS.white,
    fontWeight: 600,
  },
};

const titleProps: TypographyProps = {
  variant: 'h1',
  fontSize: '22px',
  fontWeight: '600',
  marginBottom: '10px',
};

const chipStyles: ChipProps['style'] = {
  fontWeight: 600,
  margin: '4px',
  padding: '2px',
};

const checkIconProps: IconProps['sx'] = {
  style: {
    color: 'green',
    marginRight: '8px',
  },
};

const mainIconStyles: IconProps['style'] = {
  backgroundColor: 'rgba(225, 225, 225, 0.25)',
  padding: '19px',
  borderRadius: '35px',
  height: '70px',
  width: '70px',
};

const mainPaperContainerStyles: PaperProps['sx'] = {
  borderRadius: '20px',
  padding: '15px',
  marginTop: '15px',
};

const subPaperContainerProps: PaperProps['sx'] = {
  elevation: 2,
  sx: {
    padding: '20px',
    margin: '15px',
    borderRadius: '20px',
    marginBottom: '30px',
  },
};

const defaultContainerStyles: BoxProps['sx'] = {
  display: 'flex',
  alignItems: 'center',
  padding: '20px',
  marginBottom: '5px',
  borderRadius: '7px',
  color: TP_COLORS.white,
};

const planBoxStyles: BoxProps['sx'] = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  p: '12px',
  height: '200px',
  backgroundColor: TP_COLORS.blue.light,
  color: TP_COLORS.white,
};

const planHilightsBoxStyles: BoxProps['sx'] = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  p: '12px',
  height: '200px',
  backgroundColor: '#F1F5F9',
  color: TP_COLORS.blue.light,
};

const stackProps: StackProps = {
  flexDirection: 'row',
  alignItems: 'center',
  margin: '5px 0 3px 20px',
};

interface IState {
  diagnosticScore: string;
  diagnosticGrade: string;
}

const getGrade = (score) => {
  if (score >= 90) {
    return 'A*';
  }
  if (score >= 80) {
    return 'A';
  }
  if (score >= 70) {
    return 'B';
  }
  if (score >= 60) {
    return 'C';
  }
  if (score >= 50) {
    return 'D';
  }
  if (score >= 40) {
    return 'E';
  }
  if (score >= 30) {
    return 'F';
  }
  if (score >= 20) {
    return 'G';
  }
  if (score < 20) {
    return 'PE';
  }
  return '';
};

export const getCurrencySign = (currency: string): string => {
  switch (currency) {
    case 'usd':
      return '$';
    case 'sgd':
      return 'S$';
    case 'eur':
      return '€';
    default:
      return currency.toUpperCase();
  }
};

const Diagnostic: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = isMobileScreen();

  // State
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const [state, setState] = useState<IState>({
    diagnosticScore: '',
    diagnosticGrade: '',
  });

  // Store
  const diagnosticState = useAppSelector(getDiagnosticState);
  const subTopicState = useAppSelector(getSubTopicState);
  const stripeState = useAppSelector(getStripeState);
  const authState = useAppSelector(getAuthState);

  const { diagnostic, recommendation, recommendationStatus } = diagnosticState;
  const { subTopic } = subTopicState;

  const submissionData = diagnostic ? diagnostic.submission : undefined;
  const topicData = subTopic && subTopic.topic ? subTopic.topic : undefined;
  const unitAmount = stripeState.productPrice
    ? stripeState.productPrice.unitAmount / 100
    : 1;

  const currency = getCurrencySign(stripeState?.productPrice?.currency || '');

  useEffect(() => {
    if (params.subtopicId) {
      dispatch(getGroupedSubTopics({ subTopicId: params.subtopicId }));
    }

    if (params.diagnosticId) {
      dispatch(getDiagnosticById(params.diagnosticId));
      dispatch(getProductPrice(params.diagnosticId));
    }
  }, [params.diagnosticId]);

  useEffect(() => {
    if (diagnostic && diagnostic.submission) {
      const diagnosticScore = (
        (diagnostic.submission.score / diagnostic.submission.totalScore) *
        100
      ).toFixed();
      const diagnosticGrade = getGrade(diagnosticScore);
      setState({ diagnosticScore, diagnosticGrade });

      if (diagnosticGrade && recommendationStatus !== 'loading') {
        dispatch(
          getRecommendation({
            subject: diagnostic.subject,
            grade: diagnosticGrade,
          })
        );
      }
    }
  }, [diagnostic]);

  const handleShareParent = (values: IShareParentState) => {
    if (values && params.diagnosticId) {
      dispatch(
        shareWithParent({
          ...values,
          diagnosticId: params.diagnosticId,
        })
      );
    }
  };

  const handleChoosePlan = () => {
    if (params.diagnosticId) {
      dispatch(
        createSession({
          diagnosticId: params.diagnosticId,
        })
      );
    }
  };

  const handleRequestCallback = () => {
    if (diagnostic && diagnostic.hubSpot && diagnostic.hubSpot.dealId) {
      dispatch(requestCallback(diagnostic.hubSpot.dealId));
    }
  };

  // RENDER
  if (diagnostic === undefined && diagnosticState.status === 'loading') {
    return (
      <div style={{ paddingTop: '30px' }}>
        <BasicLoader />
      </div>
    );
  }

  if (diagnostic) {
    const RENDER_CONTENT = () => {
      if (diagnosticState.status === 'loading') {
        return (
          <div>
            <BasicLoader />
          </div>
        );
      }

      if (submissionData) {
        const PLAN_HIGHLIGHT = (text: string) => (
          <Stack {...stackProps}>
            <CheckIcon {...checkIconProps} />
            <Typography fontWeight="600" fontSize="12px">
              {text}
            </Typography>
          </Stack>
        );

        let timeSpent = 0;
        let _minutes = 0;
        let _seconds = 0;

        if (submissionData.timerEnd && submissionData.timerStart) {
          timeSpent = Math.floor(
            (submissionData.timerEnd - submissionData.timerStart) / 1000
          );
        }

        _minutes = Math.floor(timeSpent / 60);
        _seconds = Math.floor(timeSpent % 60);

        return (
          <div>
            <Paper sx={mainPaperContainerStyles}>
              {/* Summary Section */}
              <Paper {...subPaperContainerProps}>
                <Typography {...titleProps}>
                  {diagnostic.fullName || ''}
                </Typography>
                <Typography marginBottom="15px">
                  Congratulations on successfully completing the assessment.
                  Your effort is truly commendable and every score holds
                  significance.
                </Typography>
                <Grid container alignItems="flex-start" spacing={2}>
                  <Grid item xs={12} sm={12} md={6} lg={4}>
                    <Box
                      sx={{
                        ...defaultContainerStyles,
                        background:
                          'linear-gradient(.25turn, #3478EE, 10%, #8FB2F3)',
                      }}
                    >
                      <PercentIcon style={mainIconStyles} />
                      <div style={{ marginLeft: '15px' }}>
                        <Typography
                          variant="h5"
                          fontSize="26px"
                          fontWeight="800"
                        >
                          {state.diagnosticScore}%
                        </Typography>
                        <Typography fontSize="15px">Your score</Typography>
                      </div>
                    </Box>
                    <Typography fontSize="15px" marginLeft="10px">
                      * Recommended for IGCSE{' '}
                      {subTopic?.diagnostic?.recommendScore || 70}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={4}>
                    <Box
                      sx={{
                        ...defaultContainerStyles,
                        background:
                          'linear-gradient(.25turn, #FE793B, 10%, #F3C2AA)',
                      }}
                    >
                      <AlarmIcon style={mainIconStyles} />
                      <div style={{ marginLeft: '15px' }}>
                        <Stack flexDirection="row" alignItems="flex-end">
                          <Typography
                            variant="h5"
                            fontSize="26px"
                            fontWeight="800"
                            lineHeight="1.1"
                          >
                            {_minutes.toString().padStart(2, '0')}:
                            {_seconds.toString().padStart(2, '0')}
                          </Typography>
                          <Typography fontSize="15px" marginLeft="5px">
                            min
                          </Typography>
                        </Stack>
                        <Typography fontSize="15px">Time spent</Typography>
                      </div>
                    </Box>

                    <Typography fontSize="15px" marginLeft="10px">
                      * Recommended time{' '}
                      {subTopic?.diagnostic?.recommendTime || 10}:00 min
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={4}>
                    <Box
                      sx={{
                        ...defaultContainerStyles,
                        background:
                          'linear-gradient(.25turn, #611FBE, 10%, #A882E3)',
                      }}
                    >
                      <TrendingUpIcon style={mainIconStyles} />
                      <Stack
                        flexDirection="row"
                        alignItems="flex-end"
                        marginLeft="15px"
                      >
                        <Typography
                          variant="h5"
                          fontSize="66px"
                          fontWeight="800"
                          lineHeight="0.8"
                        >
                          {state.diagnosticGrade === 'PE' ? (
                            <Tooltip
                              title="Potential to Excel"
                              placement="top-end"
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: TP_COLORS.tutopiyaGreenMain.light,
                                    color: TP_COLORS.white,
                                    fontSize: '14px',
                                  },
                                },
                              }}
                            >
                              <div>{state.diagnosticGrade}</div>
                            </Tooltip>
                          ) : (
                            state.diagnosticGrade
                          )}
                        </Typography>
                        <Typography fontSize="15px">Grade</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Recommendations for Growth */}
              <Paper {...subPaperContainerProps}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={7} lg={8}>
                    <Typography {...titleProps}>
                      Recommendations for Growth
                    </Typography>
                    <Typography paddingTop="10px">
                      {recommendationStatus === 'loading' ? (
                        <div style={{ paddingTop: '30px' }}>
                          <BasicLoader />
                        </div>
                      ) : (
                        recommendation?.feedback ||
                        "Your performance in the diagnostic assessment is promising but indicates areas for refinement. Continue building on your understanding of key concepts, seek clarification when necessary, and reinforce your knowledge through additional practice. Utilize available resources for continued improvement. Let's collaborate to elevate your achievements."
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={5} lg={4}>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/detailed-report-girl.png`}
                      alt="countdown-timer"
                      style={{ width: '70%', maxWidth: '90vw' }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Strengths and improvements */}
              <Paper {...subPaperContainerProps}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <Typography {...titleProps}>Strengths</Typography>
                    <Typography marginBottom="8px">
                      {diagnostic.strengths?.length
                        ? 'You have demonstrated proficiency in'
                        : "Every test is progress; no specific strengths identified in this test, but your dedication to learning is key.Keep pushing forward—we're here for you!"}
                    </Typography>
                    {diagnostic.strengths?.length
                      ? diagnostic.strengths.map((item, index) => (
                          <Chip
                            key={index}
                            label={item}
                            color="success"
                            style={chipStyles}
                          />
                        ))
                      : null}
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <Typography {...titleProps}>
                      Areas for improvements
                    </Typography>
                    <Typography marginBottom="8px">
                      {diagnostic.improvements?.length
                        ? 'Focus on understanding'
                        : 'Fantastic progress! Well done. Sustain this momentum with practice and revision. Check the practice plan to maintain and build on your success.'}
                    </Typography>
                    {diagnostic.improvements?.length
                      ? diagnostic.improvements.map((item, index) => (
                          <Chip
                            key={index}
                            label={item}
                            color="warning"
                            style={chipStyles}
                          />
                        ))
                      : null}
                  </Grid>
                </Grid>
              </Paper>

              {/* Lesson Plan Table */}
              <div style={{ padding: '15px', marginBottom: '15px' }}>
                <Typography {...titleProps}>Recommended Lesson Plan</Typography>
                <Typography marginBottom="20px">
                  Thank you for completing Tutopiya Diagnostic Assessment. Your
                  performance has been impressive. To further enhance and master
                  your skills, we have designed a personalized plan to boost
                  your exam results.
                </Typography>

                {diagnostic.lessonPlan && (
                  <LessonPlanTable lessonPlan={diagnostic.lessonPlan} />
                )}
              </div>

              {/* Plan Pricing Section */}
              <div style={{ padding: '15px', marginBottom: '15px' }}>
                <Grid container alignItems="center">
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Box
                      sx={{
                        ...planBoxStyles,
                        borderTopLeftRadius: '15px',
                        borderBottomLeftRadius: '15px',
                      }}
                    >
                      <Typography variant="h5" fontSize="23px" fontWeight="300">
                        No.of Hours
                      </Typography>
                      <Typography variant="h5" fontSize="70px" fontWeight="900">
                        {diagnostic.recommendedHours || 1}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Box sx={planHilightsBoxStyles}>
                      {PLAN_HIGHLIGHT('Curated Study Resources')}
                      {PLAN_HIGHLIGHT('Practice Papers')}
                      {PLAN_HIGHLIGHT('AI Exam Assistance')}
                      {PLAN_HIGHLIGHT('1-To-1 Classes')}
                      {PLAN_HIGHLIGHT('Customized Study Plans')}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Box sx={planHilightsBoxStyles}>
                      {PLAN_HIGHLIGHT('Exam Preparation')}
                      {PLAN_HIGHLIGHT('Mock Tests')}
                      {PLAN_HIGHLIGHT('Progress Tracking')}
                      {PLAN_HIGHLIGHT('Flexible Schedule')}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={3}>
                    <Box
                      sx={{
                        ...planBoxStyles,
                        borderTopRightRadius: '15px',
                        borderBottomRightRadius: '15px',
                      }}
                    >
                      {(
                        unitAmount * (diagnostic.recommendedHours || 1)
                      ).toString().length < 5 ? (
                        <Typography
                          variant="h5"
                          fontSize="50px"
                          fontWeight="800"
                        >
                          {currency}{' '}
                          {unitAmount * (diagnostic.recommendedHours || 1)}
                        </Typography>
                      ) : (
                        <Stack flexDirection="row" alignItems="flex-end">
                          <Typography
                            variant="h5"
                            fontSize="25px"
                            fontWeight="600"
                            margin="0 5px 5px 0"
                          >
                            {currency}
                          </Typography>
                          <Typography
                            variant="h5"
                            fontSize="40px"
                            fontWeight="800"
                          >
                            {unitAmount * (diagnostic?.recommendedHours || 1)}
                          </Typography>
                        </Stack>
                      )}
                      <Typography
                        variant="h5"
                        fontSize="23px"
                        fontWeight="300"
                        marginBottom="10px"
                      >
                        {currency} {unitAmount}/Hour
                      </Typography>
                      <Button
                        {...secondaryButtonProps}
                        size="large"
                        onClick={handleChoosePlan}
                      >
                        Choose Plan
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                <Typography marginTop="15px">
                  Note:The above grade is an indication only based on your
                  performance on this assessment
                </Typography>
              </div>
            </Paper>

            {/* MODALS */}
            {isShareModalOpen && (
              <ShareModal
                open={isShareModalOpen}
                onClose={() => setShareModalOpen(false)}
                onConfirm={handleShareParent}
              />
            )}
          </div>
        );
      }
      return null;
    };

    return (
      <div>
        {params.subtopicId ? (
          <MobileHeader
            leftContents={{
              type: 'subtopic-nav-bar',
            }}
          />
        ) : (
          <MobileHeader
            leftContents={{
              type: 'backButton',
              iconButtonProps: {
                onClick: () => navigate(-1),
              },
            }}
          />
        )}
        <TopicHeader
          subject={topicData?.subject || ''}
          topic={topicData?.name || ''}
          subTopics={[subTopic?.name || '', 'Detailed Report']}
        />
        <Box component="div" sx={{ padding: { xs: '9px', sm: '15px' } }}>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            margin={`0px ${isMobile ? '20px' : '0px'}`}
          >
            <Button
              {...secondaryButtonProps}
              onClick={() => {
                navigate(
                  `${process.env.PUBLIC_URL}/dashboard/my-quizzes/subtopic/${params.subtopicId}/${diagnostic.submission._id}/${params.pattern}`
                );
              }}
            >
              Back
            </Button>
            <div style={{ display: 'flex', marginLeft: '10px' }}>
              <Button {...secondaryButtonProps} onClick={handleRequestCallback}>
                Request a Callback
              </Button>

              <Button
                size="small"
                color="primary"
                variant="contained"
                sx={{
                  fontWeight: 600,
                  marginLeft: '10px',
                  maxWidth: { xs: 'unset', sm: '400px' },
                }}
                startIcon={<WhatsAppIcon sx={whatsappIconStyles} />}
                onClick={() => setShareModalOpen(true)}
              >
                Share with Parent
              </Button>
            </div>
          </Stack>

          {RENDER_CONTENT()}
        </Box>
      </div>
    );
  }

  return null;
};

export default Diagnostic;
